<template>
  <div class="modal-overlay">
    <div class="mmodal-content" style="max-width: 80vw">
      <div class="modal-header">
        <h3>{{ editingRecord ? "Edit" : "Record" }} Postnatal Visit</h3>
        <button type="button" class="close" @click="closeModal">&times;</button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="saveRecord">
          <h4 class="section-title">Visit Details</h4>
          <div class="form-grid">
            <div class="form-group">
              <label>Visit Date *</label>
              <input type="datetime-local" v-model="form.visit_date" required class="form-control" />
            </div>
            <div class="form-group">
              <label>Visit Schedule</label>
              <select v-model="form.visit_schedule" class="form-control">
                <option value="">Select</option>
                <option value="Day 3">Day 3</option>
                <option value="Day 6">Day 6</option>
                <option value="Week 6">Week 6</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div class="form-group">
              <label>Days Post Delivery</label>
              <input type="number" v-model="form.days_post_delivery" min="0" class="form-control" />
            </div>
          </div>

          <h4 class="section-title">Mother's Assessment</h4>
          <div class="form-grid">
            <div class="form-group">
              <label>BP</label>
              <div class="bp-inputs">
                <input type="number" v-model="form.bp_systolic" placeholder="Systolic" class="form-control" />
                <span class="bp-separator">/</span>
                <input type="number" v-model="form.bp_diastolic" placeholder="Diastolic" class="form-control" />
              </div>
            </div>
            <div class="form-group">
              <label>Pulse</label>
              <input type="number" v-model="form.pulse" class="form-control" />
            </div>
            <div class="form-group">
              <label>Temperature (°C)</label>
              <input type="number" v-model="form.temperature" step="0.1" class="form-control" />
            </div>
            <div class="form-group">
              <label>Fundal Height</label>
              <input type="text" v-model="form.fundal_height" class="form-control" />
            </div>
            <div class="form-group">
              <label>Lochia</label>
              <select v-model="form.lochia" class="form-control">
                <option value="">Select</option>
                <option value="Rubra">Rubra</option>
                <option value="Serosa">Serosa</option>
                <option value="Alba">Alba</option>
                <option value="Heavy/Excessive">Heavy/Excessive</option>
                <option value="None">None</option>
              </select>
            </div>
            <div class="form-group">
              <label>Perineum Condition</label>
              <input type="text" v-model="form.perineum_condition" class="form-control" />
            </div>
            <div class="form-group">
              <label>Breast Condition</label>
              <input type="text" v-model="form.breast_condition" class="form-control" />
            </div>
            <div class="form-group">
              <label>Psychological State</label>
              <input type="text" v-model="form.psychological_state" placeholder="e.g. Stable, low mood" class="form-control" />
            </div>
            <div class="form-group full-width">
              <label>Mother's Complaints</label>
              <textarea v-model="form.mother_complaints" class="form-control" rows="2"></textarea>
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="form.family_planning_discussed" />
                Family planning discussed
              </label>
            </div>
            <div class="form-group" v-if="form.family_planning_discussed">
              <label>Method</label>
              <input type="text" v-model="form.family_planning_method" class="form-control" />
            </div>
          </div>

          <h4 class="section-title">Baby's Assessment</h4>
          <div class="form-grid">
            <div class="form-group">
              <label>Weight (g)</label>
              <input type="number" v-model="form.baby_weight_grams" min="0" class="form-control" />
            </div>
            <div class="form-group">
              <label>Temperature (°C)</label>
              <input type="number" v-model="form.baby_temperature" step="0.1" class="form-control" />
            </div>
            <div class="form-group">
              <label>Feeding Status</label>
              <select v-model="form.feeding_status" class="form-control">
                <option value="">Select</option>
                <option value="Exclusive breastfeeding">Exclusive breastfeeding</option>
                <option value="Mixed">Mixed</option>
                <option value="Formula">Formula</option>
              </select>
            </div>
            <div class="form-group">
              <label>Cord Status</label>
              <select v-model="form.cord_status" class="form-control">
                <option value="">Select</option>
                <option value="Healing well">Healing well</option>
                <option value="Discharge">Discharge</option>
                <option value="Fallen off">Fallen off</option>
              </select>
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="form.jaundice_present" />
                Jaundice present
              </label>
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="form.immunization_up_to_date" />
                Immunization up to date
              </label>
            </div>
            <div class="form-group full-width">
              <label>Baby Danger Signs</label>
              <textarea v-model="form.baby_danger_signs" class="form-control" rows="2"></textarea>
            </div>
          </div>

          <h4 class="section-title">Plan</h4>
          <div class="form-grid">
            <div class="form-group full-width">
              <label>Complications</label>
              <textarea v-model="form.complications" class="form-control" rows="2"></textarea>
            </div>
            <div class="form-group full-width">
              <label>Plan</label>
              <textarea v-model="form.plan" class="form-control" rows="2"></textarea>
            </div>
            <div class="form-group full-width">
              <label>Notes</label>
              <textarea v-model="form.notes" class="form-control" rows="2"></textarea>
            </div>
            <div class="form-group">
              <label>Seen By</label>
              <input type="text" v-model="form.seen_by" class="form-control" />
            </div>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              {{ saving ? "Saving..." : (editingRecord ? "Update" : "Save") + " Visit" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { generalApiRequest } from "../../../../api/apiIndex";

export default {
  name: "PncVisitModal",
  props: {
    visitId: { type: [Number, String], default: null },
    editRecord: { type: Object, default: null },
  },
  data() {
    return {
      saving: false,
      editingRecord: null,
      form: this.emptyForm(),
    };
  },
  created() {
    if (this.editRecord) {
      this.editingRecord = this.editRecord;
      this.form = {
        ...this.emptyForm(),
        ...this.editRecord,
        visit_date: this.toLocalDateTime(this.editRecord.visit_date),
      };
    }
  },
  methods: {
    emptyForm() {
      return {
        visit_date: this.toLocalDateTime(new Date()),
        visit_schedule: "",
        days_post_delivery: "",
        bp_systolic: "",
        bp_diastolic: "",
        pulse: "",
        temperature: "",
        fundal_height: "",
        lochia: "",
        perineum_condition: "",
        breast_condition: "",
        psychological_state: "",
        mother_complaints: "",
        family_planning_discussed: false,
        family_planning_method: "",
        baby_weight_grams: "",
        baby_temperature: "",
        feeding_status: "",
        cord_status: "",
        jaundice_present: false,
        baby_danger_signs: "",
        immunization_up_to_date: false,
        complications: "",
        plan: "",
        notes: "",
        seen_by: "",
      };
    },
    toLocalDateTime(value) {
      if (!value) return "";
      const d = new Date(value);
      if (isNaN(d.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    closeModal() {
      this.$emit("close");
    },
    async saveRecord() {
      this.saving = true;
      try {
        const payload = { ...this.form, visit_id: this.visitId };
        let response;
        if (this.editingRecord) {
          response = await generalApiRequest(`pnc_visits_records/${this.editingRecord.id}`, "PUT", payload, 1);
        } else {
          response = await generalApiRequest("pnc_visits_records", "POST", payload, 1);
        }
        if (response.success) {
          this.$emit("saved", response.data);
          this.closeModal();
        } else {
          alert(response.message || "Failed to save postnatal visit");
        }
      } catch (error) {
        console.error("Error saving PNC visit:", error);
        alert("An error occurred while saving the postnatal visit");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.mmodal-content { background: white; border-radius: 10px; max-height: 90vh; overflow-y: auto; display: flex; flex-direction: column; }
.modal-header { padding: 20px; background: linear-gradient(135deg, #319795 0%, #285e61 100%); color: white; display: flex; justify-content: space-between; align-items: center; }
.modal-header h3 { margin: 0; font-size: 20px; }
.close { background: rgb(233, 22, 22); border: 1px solid rgb(182, 178, 178); font-size: 1.5rem; color: white; line-height: 1; cursor: pointer; }
.modal-body { padding: 30px; overflow-y: auto; flex-grow: 1; }
.section-title { font-size: 14px; color: #285e61; margin: 18px 0 10px; padding-bottom: 6px; border-bottom: 2px solid #e6fffa; }
.section-title:first-child { margin-top: 0; }
.form-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-bottom: 10px; }
.form-group { margin-bottom: 15px; }
.form-group.full-width { grid-column: 1 / -1; }
.form-group label { display: block; margin-bottom: 5px; font-weight: 600; color: #495057; }
.checkbox-label { display: flex !important; align-items: center; gap: 8px; margin-top: 28px; }
.form-control { width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px; font-size: 14px; }
.bp-inputs { display: flex; align-items: center; gap: 10px; }
.bp-inputs .form-control { flex: 1; }
.bp-separator { font-weight: bold; color: #495057; }
.modal-actions { display: flex; justify-content: flex-end; gap: 15px; }
.btn { padding: 10px 25px; border: none; border-radius: 5px; cursor: pointer; font-weight: 600; }
.btn-secondary { background: #6c757d; color: white; }
.btn-secondary:hover { background: #5a6268; }
.btn-primary { background: #285e61; color: white; }
.btn-primary:hover { background: #234e52; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
@media (max-width: 1200px) { .form-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 768px) { .form-grid { grid-template-columns: 1fr; } }
</style>
